import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class CurrencyService {
  /**
   * Converts a pound amount to pence (integer).
   * @param amount The amount in pounds (e.g., 1.23).
   * @returns The amount in pence (e.g., 123).
   */
  toPence(amount: number): number {
    return Math.round(amount * 100);
  }

  /**
   * Converts a pence amount (integer) to pounds.
   * @param pence The amount in pence (e.g., 123).
   * @returns The amount in pounds (e.g., 1.23).
   */
  fromPence(pence: number): number {
    return pence / 100;
  }

  /**
   * Rounds a pence value to the nearest whole penny.
   * Accepts fractional pence so accrual engines can defer rounding until the
   * relevant subtotal or final total is materialised.
   */
  roundPence(pence: number): number {
    return Math.round(pence);
  }

  /**
   * Performs safe multiplication of a currency amount by a number.
   * @param amountA The currency amount in pounds.
   * @param amountB The multiplier.
   * @returns The result in pounds.
   */
  multiply(amountA: number, amountB: number): number {
    const penceA = this.toPence(amountA);
    const resultPence = penceA * amountB;
    return this.fromPence(resultPence);
  }

  /**
   * Adds two amounts in pounds, performing the operation in pence to avoid floating-point errors.
   * @param amountA The first amount in pounds.
   * @param amountB The second amount in pounds.
   * @returns The sum in pounds.
   */
  add(amountA: number, amountB: number): number {
    const penceA = this.toPence(amountA);
    const penceB = this.toPence(amountB);
    return this.fromPence(penceA + penceB);
  }

  /**
   * Subtracts amountB from amountA in pounds, performing the operation in pence.
   * @param amountA The amount to subtract from.
   * @param amountB The amount to subtract.
   * @returns The difference in pounds.
   */
  subtract(amountA: number, amountB: number): number {
    const penceA = this.toPence(amountA);
    const penceB = this.toPence(amountB);
    return this.fromPence(penceA - penceB);
  }

  /**
   * Allocates an amount based on a list of ratios.
   * Ensures the sum of the allocated parts equals the total amount, handling remainders.
   * @param amount The total amount to allocate in pounds.
   * @param ratios A list of ratios (e.g., [1, 2, 3]).
   * @returns The allocated amounts in pounds.
   */
  allocate(amount: number, ratios: number[]): number[] {
    if (ratios.length === 0) return [];

    const totalPence = this.toPence(amount);
    const totalRatio = ratios.reduce((sum, r) => sum + r, 0);

    if (totalRatio === 0) return ratios.map(() => 0);

    let remainder = totalPence;
    const results: number[] = [];

    for (let i = 0; i < ratios.length; i++) {
      const ratio = ratios[i];
      const share = Math.floor((totalPence * ratio) / totalRatio);
      results.push(share);
      remainder -= share;
    }

    // Distribute the remainder to the first items
    for (let i = 0; i < remainder; i++) {
      results[i]++;
    }

    return results.map(p => this.fromPence(p));
  }

  /**
   * Calculates interest in exact pence without rounding.
   * This is used by accrual engines that need to preserve fractional pennies
   * across daily calculations and round only at the row or total level.
   */
  calculateInterestExactPence(principalPence: number, ratePercent: number, days: number, daysInYear: number): number {
    if (principalPence === 0 || ratePercent === 0) {
      return 0;
    }

    return (principalPence * (ratePercent / 100) * days) / daysInYear;
  }

  /**
   * Calculates interest in pence to avoid floating-point errors.
   * @param principalPence The principal amount in pence.
   * @param ratePercent The annual interest rate as a percentage (e.g., 5 for 5%).
   * @param days The number of days to calculate interest for.
   * @param daysInYear The number of days in the year (e.g., 365).
   * @returns The calculated interest, rounded to the nearest penny, as an integer.
   */
  calculateInterest(principalPence: number, ratePercent: number, days: number, daysInYear: number): number {
    return this.roundPence(
      this.calculateInterestExactPence(principalPence, ratePercent, days, daysInYear)
    );
  }
}
